import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./LearningPaths.css";
import LearningPathPage from "./LearningPathPage";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { ClipLoader } from "react-spinners";
import Footer from "../components/Footer";

const baseUrl = process.env.REACT_APP_API_BASE_URL;

const learningPathGroups = {
  Algorithms: [
    "WARM_UP",
    "TWO_POINTERS",
    "SLIDING_WINDOW",
    "RECURSIVE",
    "STACK",
    // "QUEUE",
    "DFS",
    "BFS",
    // "BINARY_SEARCH",
    // "DYNAMIC_PROGRAMMING",
    // "GREEDY",
    // "BACKTRACKING",
    // "HASH_MAP",
    // "MERGE_SORT",
    // "SORTING",
  ],
  Companies: ["AMAZON", "GOOGLE", "FACEBOOK", "MICROSOFT"],
  "Interview Prep": ["INTERVIEW_FUNDAMENTALS"],
};

const formatLabel = (label) =>
  label
    .toLowerCase()
    .split("_")
    .map((word) => word[0].toUpperCase() + word.slice(1))
    .join(" ");

const LearningPaths = () => {
  const allPaths = Object.values(learningPathGroups).flat();
  const location = useLocation();
  const [selectedPath, setSelectedPath] = useState(
    location.state?.selectedPath || null
  );
  const [progressByPath, setProgressByPath] = useState({});
  const [problemsByPath, setProblemsByPath] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [role, setRole] = useState(null);
  const navigate = useNavigate();

  const isFreeUser = role === "FREE_USER";
  const isPaidUser = role === "PAID_USER";
  const isPremium = ["PREMIUM_USER", "ADMIN"].includes(role);

  const paidUserAllowedPaths = ["WARM_UP", "TWO_POINTERS"];

  const normalizePathSlugToPath = (slug) =>
    slug.toUpperCase().replace(/-/g, "_");

  useEffect(() => {
    if (location.state?.selectedPath) {
      const restoredPath = normalizePathSlugToPath(location.state.selectedPath);
      setSelectedPath(restoredPath);
    }
  }, [location.state?.selectedPath]);

  useEffect(() => {
    const fetchUserRoleAndProgress = async () => {
      const token = localStorage.getItem("token");
      let userId = null;
      let progress = [];

      if (token) {
        const decoded = jwtDecode(token);
        userId = decoded.id;

        try {
          const userRes = await axios.get(
            `${baseUrl}/users/account/${userId}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          setRole(userRes.data.role || "FREE_USER");
        } catch (err) {
          console.error("Failed to fetch role:", err);
          setRole("FREE_USER");
        }

        try {
          const progressRes = await axios.get(`${baseUrl}/progress/${userId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          progress = Array.isArray(progressRes.data) ? progressRes.data : [];
        } catch (err) {
          if (err.response?.status === 404) {
            progress = [];
          } else {
            throw err;
          }
        }
      } else {
        setRole("FREE_USER");
      }

      const pathProgress = {};
      const problemsMap = {};

      await Promise.all(
        allPaths.map(async (path) => {
          const pathSlug = path.toLowerCase().replace(/_/g, "-");
          const res = await axios.get(
            `${baseUrl}/problems/learning-paths/${pathSlug}`
          );
          const problems = res.data;

          const problemsWithProgress = problems.map((problem) => {
            const beginnerDone = progress.some(
              (p) =>
                p.problemId === problem.id &&
                p.level === "BEGINNER" &&
                p.completed
            );
            const proDone = progress.some(
              (p) =>
                p.problemId === problem.id && p.level === "PRO" && p.completed
            );

            return {
              ...problem,
              progress: { BEGINNER: beginnerDone, PRO: proDone },
            };
          });

          const totalSolutions = problemsWithProgress.length * 2;
          const completedSolutions = problemsWithProgress.reduce(
            (count, p) =>
              count + (p.progress.BEGINNER ? 1 : 0) + (p.progress.PRO ? 1 : 0),
            0
          );

          pathProgress[path] = totalSolutions
            ? Math.round((completedSolutions / totalSolutions) * 100)
            : 0;

          problemsMap[path] = problemsWithProgress;
        })
      );

      setProgressByPath(pathProgress);
      setProblemsByPath(problemsMap);
      setIsLoading(false);
    };

    fetchUserRoleAndProgress();
  }, []);

  useEffect(() => {
    const initialExpanded = {};

    if (location.state?.expandedGroups) {
      setExpandedGroups(location.state.expandedGroups); // ✅ restore
      return;
    }

    for (let group in learningPathGroups) {
      initialExpanded[group] = false;
    }

    setExpandedGroups(initialExpanded);
  }, [location.state?.expandedGroups]);

  const toggleGroup = (group) => {
    if (!isFreeUser) {
      setExpandedGroups((prev) => ({
        ...prev,
        [group]: !prev[group],
      }));
    }
  };

  return (
    <div className="learning-paths-wrapper">
      <div className={`sidebar ${isFreeUser ? "blurred-group" : ""}`}>
        <h2 className="sidebar-title">Learning Paths</h2>
        {Object.entries(learningPathGroups).map(([groupName, paths]) => {
          const isLockedGroupForPaidUser =
            isPaidUser && ["Companies", "Interview Prep"].includes(groupName);

          const groupContent = (
            <div
              className={`sidebar-group ${
                isFreeUser || isLockedGroupForPaidUser ? "locked-group" : ""
              }`}
            >
              <button
                className="group-toggle"
                onClick={() => toggleGroup(groupName)}
              >
                {groupName}
                <span className="path-page arrow">
                  {expandedGroups[groupName] ? "▾" : "▸"}
                </span>
              </button>

              {expandedGroups[groupName] && (
                <div className="group-paths">
                  {paths.map((path) => {
                    const isClickable =
                      isPremium ||
                      (isPaidUser &&
                        groupName === "Algorithms" &&
                        paidUserAllowedPaths.includes(path));

                    const isBlurred =
                      isFreeUser ||
                      (isPaidUser &&
                        (groupName !== "Algorithms" ||
                          !paidUserAllowedPaths.includes(path)));

                    return isBlurred ? (
                      <div key={path} className="locked-path-wrapper">
                        <div className="sidebar-item blurred-path">
                          <button disabled className="sidebar-button">
                            {formatLabel(path)}
                          </button>
                          <div className="progress-bar-container">
                            <div
                              className="progress-bar-fill"
                              style={{ width: `${progressByPath[path] || 0}%` }}
                            >
                              <span className="progress-label-inside">
                                {progressByPath[path] || 0}%
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="locked-path-overlay">
                          <button onClick={() => navigate("/membership")}>
                            Upgrade to Unlock
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div
                        key={path}
                        className={`sidebar-item ${
                          progressByPath[path] === 100 ? "path-complete" : ""
                        }`}
                      >
                        <button
                          className={`sidebar-button ${selectedPath === path ? "active" : ""}`}
                          onClick={() => setSelectedPath(path)}
                        >
                          {formatLabel(path)}
                        </button>
                        <div className="progress-bar-container">
                          <div
                            className="progress-bar-fill"
                            style={{ width: `${progressByPath[path] || 0}%` }}
                          >
                            <span className="progress-label-inside">
                              {progressByPath[path] || 0}%
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );

          return isLockedGroupForPaidUser ? (
            <div key={groupName} className="locked-group-wrapper">
              {groupContent}
              <div className="locked-overlay">
                <button onClick={() => navigate("/membership")}>
                  Upgrade To Unlock
                </button>
              </div>
            </div>
          ) : (
            <div key={groupName}>{groupContent}</div>
          );
        })}

        {/* {isFreeUser && (
          <button
            className="upgrade-button"
            style={{ marginTop: "20px" }}
            onClick={() => navigate("/membership")}
          >
            Upgrade to Unlock
          </button>
        )} */}
      </div>

      <div className="content-box-with-footer">
        <div className="content-box">
          {isLoading ? (
            <div className="spinner-wrapper">
              <ClipLoader color="#1abc9c" size={60} />
            </div>
          ) : selectedPath ? (
            <LearningPathPage
              pathId={selectedPath.toLowerCase().replace(/_/g, "-")}
              problems={problemsByPath[selectedPath] || []}
              expandedGroups={expandedGroups}
            />
          ) : (
            <div className="content-placeholder">
              {isFreeUser ? (
                <>
                  <h2>This feature is for paid users only.</h2>
                  <p>
                    Upgrade to unlock our guided learning paths and track
                    progress.
                  </p>
                  <button
                    className="upgrade-button"
                    style={{
                      padding: "10px 20px",
                      fontSize: "1.1rem",
                      marginTop: "20px",
                      backgroundColor: "#007bff",
                      color: "#fff",
                      border: "none",
                      borderRadius: "5px",
                      cursor: "pointer",
                    }}
                    onClick={() => navigate("/membership")}
                  >
                    Upgrade Now
                  </button>
                </>
              ) : (
                <>
                  <h2>Please select a learning path from the sidebar</h2>
                  {isPaidUser && (
                    <button
                      className="upgrade-button"
                      style={{
                        marginTop: "20px",
                        backgroundColor: "#f39c12",
                        color: "#fff",
                        padding: "10px 20px",
                        borderRadius: "6px",
                        border: "none",
                        fontSize: "0.95rem",
                        fontWeight: "bold",
                        cursor: "pointer",
                      }}
                      onClick={() => navigate("/membership")}
                    >
                      Upgrade to Unlock All Learning Paths
                    </button>
                  )}
                  {!selectedPath && !isLoading && (
                    <div className="sidebar-pointer-animation">
                      👈 Select a Learning Path!
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
        <Footer />
      </div>
    </div>
  );
};

export default LearningPaths;
